import { getAuthSnapshot, supabase } from './supabase';

type LibraryPreferences = {
  filter?: string;
  sort?: string;
  size?: string;
  detailedSort?: string;
  sortPrimary?: string;
  sortSecondary?: string;
  sortTertiary?: string;
  groupBy?: string;
  settingsCollapsed?: boolean;
};

const LOCAL_KEY = 'empyrean-v2-library-preferences';
let preferences: LibraryPreferences = readLocal();
let hydrated = false;
let saveTimer: number | null = null;
let pendingCloudSave = false;
const dirtyKeys = new Set<keyof LibraryPreferences>();

function readLocal(): LibraryPreferences {
  try {
    const parsed = JSON.parse(localStorage.getItem(LOCAL_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed as LibraryPreferences : {};
  } catch {
    return {};
  }
}

function writeLocal(next: LibraryPreferences, markDirty = false) {
  preferences = { ...preferences, ...next };
  if (markDirty) (Object.keys(next) as Array<keyof LibraryPreferences>).forEach((key) => dirtyKeys.add(key));
  try { localStorage.setItem(LOCAL_KEY, JSON.stringify(preferences)); } catch {}
}

function emitPreferences() {
  window.dispatchEvent(new CustomEvent('library-preferences-updated', { detail: { ...preferences } }));
}

function selectKind(select: HTMLSelectElement): keyof LibraryPreferences | null {
  const explicit = select.dataset.libraryPref as keyof LibraryPreferences | undefined;
  if (explicit) return explicit;
  if (select.closest('.advanced-library-sort')) return 'detailedSort';
  const values = [...select.options].map((option) => option.value);
  if (values.includes('active') && values.includes('archived')) return 'filter';
  if (values.includes('updated') && values.includes('rating')) return 'sort';
  if (values.includes('small') && values.includes('medium') && values.includes('large')) return 'size';
  return null;
}

function applyPreference(select: HTMLSelectElement) {
  const kind = selectKind(select);
  if (!kind || kind === 'settingsCollapsed') return;
  let value = preferences[kind];
  if (kind === 'sortPrimary' && !value && preferences.detailedSort) value = preferences.detailedSort;
  if (typeof value !== 'string' || !value || select.value === value || ![...select.options].some((option) => option.value === value)) return;
  select.value = value;
  select.dispatchEvent(new Event('change', { bubbles: true }));
}

function setCollapsed(library: HTMLElement, collapsed: boolean) {
  library.classList.toggle('library-settings-collapsed', collapsed);
  const toggle = library.querySelector<HTMLButtonElement>('.library-settings-toggle');
  if (!toggle) return;
  toggle.setAttribute('aria-expanded', String(!collapsed));
  toggle.setAttribute('aria-label', collapsed ? 'Expand library settings' : 'Collapse library settings');
  const label = toggle.querySelector<HTMLElement>('.library-settings-toggle-label');
  const state = toggle.querySelector<HTMLElement>('.library-settings-toggle-state');
  if (label) label.textContent = 'Library settings';
  if (state) state.textContent = collapsed ? 'Show' : 'Hide';
}

function ensureCollapseUi(library: HTMLElement) {
  const controls = library.querySelector<HTMLElement>('.v2-library-controls');
  const actionbar = library.querySelector<HTMLElement>('.v2-library-actionbar');
  if (!controls || !actionbar) return;

  let shell = library.querySelector<HTMLElement>('.library-settings-command-bar');
  if (!shell) {
    shell = document.createElement('div');
    shell.className = 'library-settings-command-bar';
    controls.insertAdjacentElement('beforebegin', shell);
  }

  let toggle = library.querySelector<HTMLButtonElement>('.library-settings-toggle');
  if (!toggle) {
    toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'library-settings-toggle';
    toggle.innerHTML = '<span class="library-settings-toggle-label">Library settings</span><span class="library-settings-toggle-state">Hide</span><span class="library-settings-toggle-chevron" aria-hidden="true">⌄</span>';
    toggle.addEventListener('click', () => {
      const collapsed = !library.classList.contains('library-settings-collapsed');
      writeLocal({ settingsCollapsed: collapsed }, true);
      setCollapsed(library, collapsed);
      emitPreferences();
      scheduleCloudSave();
      window.dispatchEvent(new CustomEvent('library-settings-visibility-changed', { detail: { collapsed } }));
    });
  }

  if (toggle.parentElement !== shell) shell.prepend(toggle);
  if (actionbar.parentElement !== shell) shell.append(actionbar);

  setCollapsed(library, preferences.settingsCollapsed === true);
}

function applyAll() {
  document.querySelectorAll<HTMLSelectElement>('.v2-view--library .v2-library-controls select, .v2-view--library .advanced-library-sort select').forEach(applyPreference);
  document.querySelectorAll<HTMLElement>('.v2-view--library .v2-library').forEach(ensureCollapseUi);
}

async function loadCloudPreferences() {
  try {
    const { user } = await getAuthSnapshot();
    if (!user) return;
    const { data, error } = await supabase
      .from('archive_states')
      .select('state')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false })
      .limit(1);
    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : undefined;
    const state = row?.state && typeof row.state === 'object' ? row.state as Record<string, unknown> : {};
    const cloud = state.libraryPreferences;
    if (cloud && typeof cloud === 'object' && !Array.isArray(cloud)) {
      const incoming = cloud as LibraryPreferences;
      const merged: LibraryPreferences = { ...preferences };
      (Object.keys(incoming) as Array<keyof LibraryPreferences>).forEach((key) => {
        if (!dirtyKeys.has(key)) merged[key] = incoming[key] as never;
      });
      preferences = merged;
      writeLocal(preferences);
    }
  } catch {
    // Local preferences remain the offline fallback.
  } finally {
    hydrated = true;
    applyAll();
    emitPreferences();
    if (pendingCloudSave || dirtyKeys.size > 0) scheduleCloudSave();
  }
}

async function saveCloudPreferences() {
  if (!hydrated) {
    pendingCloudSave = true;
    return;
  }
  pendingCloudSave = false;
  try {
    const { user } = await getAuthSnapshot();
    if (!user) return;
    const { data, error } = await supabase
      .from('archive_states')
      .select('state')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false })
      .limit(1);
    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : undefined;
    const state = row?.state && typeof row.state === 'object' ? row.state as Record<string, unknown> : {};
    const payload = { state: { ...state, libraryPreferences: preferences }, updated_at: new Date().toISOString() };
    if (row) {
      const { error: updateError } = await supabase
        .from('archive_states')
        .update(payload)
        .eq('user_id', user.id);
      if (updateError) throw updateError;
    } else {
      const { error: insertError } = await supabase
        .from('archive_states')
        .insert({ user_id: user.id, ...payload });
      if (insertError) throw insertError;
    }
    dirtyKeys.clear();
  } catch {
    pendingCloudSave = true;
  }
}

function scheduleCloudSave() {
  pendingCloudSave = true;
  if (saveTimer !== null) window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => {
    saveTimer = null;
    void saveCloudPreferences();
  }, 350);
}

function handleChange(event: Event) {
  const select = event.target instanceof HTMLSelectElement ? event.target : null;
  if (!select || !select.matches('.v2-view--library .v2-library-controls select, .v2-view--library .advanced-library-sort select')) return;
  const kind = selectKind(select);
  if (!kind || kind === 'settingsCollapsed') return;
  writeLocal({ [kind]: select.value }, true);
  emitPreferences();
  scheduleCloudSave();
}

let frame = 0;
function scheduleApply() {
  if (frame) return;
  frame = requestAnimationFrame(() => {
    frame = 0;
    applyAll();
  });
}

function start() {
  document.addEventListener('change', handleChange, true);
  const observer = new MutationObserver(scheduleApply);
  observer.observe(document.body, { childList: true, subtree: true });
  applyAll();
  emitPreferences();
  void loadCloudPreferences();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
else start();
